import type { RefObject } from 'react';
import { CEW_PRESS_RELEASE_PDF_URL, CEW_REPORT_PDF_URL } from '../../lib/cew-site';
import { PdfDownloadButton } from '../shared/PdfDownloadButton';

/**
 * Small chrome pieces for `MobileMenuDrawer`'s panel — the header
 * (label + close button) and the footer PDF download links.
 * Split out to keep `MobileMenuDrawer.tsx` under the 250-line cap.
 */

interface DrawerHeaderProps {
  readonly closeButtonRef: RefObject<HTMLButtonElement>;
  readonly onClose: () => void;
}

export function DrawerHeader({ closeButtonRef, onClose }: DrawerHeaderProps) {
  return (
    <header className="flex h-14 items-center justify-between border-b border-border px-4">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-text-4">Menu</span>
      <button
        ref={closeButtonRef}
        type="button"
        onClick={onClose}
        aria-label="Close menu"
        className="flex h-11 w-11 items-center justify-center rounded-md text-text-3 transition hover:bg-ice hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
      >
        <svg
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </header>
  );
}

interface DrawerDownloadLinksProps {
  readonly onClose: () => void;
}

// CR-027/028/029/030 (Frank staging review, 2026-08-18): the report
// affordance lives in SectionNav for viewport >=1136px (matching the
// desktop CewSiteNav floor); this drawer footer is the only affordance
// below that floor — Topbar itself carries no such link. CR-034/035
// (Frank, 2026-08-28): it is now the "Download Full Report" PDF pill, and
// because the homepage rail (which carries the press release on desktop,
// CR-035) is lg-only, the drawer carries BOTH PDFs so the press release
// is reachable at every width. Visuals + `_blank` behaviour come from the
// shared `PdfDownloadButton` (DEC-044); destinations from `cew-site.ts`.
export function DrawerDownloadLinks({ onClose }: DrawerDownloadLinksProps) {
  return (
    // 280px panel − px-5 = 240px of content: `compact` (no glyph, px-3 below
    // 1280 — the drawer only exists below 1136) + `fill` keeps
    // "Download Press Release" (~224px) on one line inside the panel.
    <div className="flex flex-col gap-3 border-t border-border px-5 py-4">
      <PdfDownloadButton
        href={CEW_REPORT_PDF_URL}
        label="Download Full Report"
        variant="primary"
        density="compact"
        fit="fill"
        onClick={onClose}
      />
      <PdfDownloadButton
        href={CEW_PRESS_RELEASE_PDF_URL}
        label="Download Press Release"
        variant="secondary"
        density="compact"
        fit="fill"
        onClick={onClose}
      />
    </div>
  );
}
